/**
 * Bybit WebSocket adapter — public Linear / Spot streams, no auth required.
 *
 * Linear: wss://stream.bybit.com/v5/public/linear  → tickers.<symbol>
 * Spot:   wss://stream.bybit.com/v5/public/spot    → tickers.<symbol>
 */

import type { ExchangeWsAdapter } from "../live-pricing.types";

const RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000];
const PING_INTERVAL_MS = 20_000;

function normalizeSymbol(symbol: string): string {
  const upper = symbol.toUpperCase();
  return upper.endsWith("USDT") ? upper : `${upper}USDT`;
}

interface BybitTickerData {
  symbol?: string;
  markPrice?: string;
  indexPrice?: string;
  fundingRate?: string;
  nextFundingTime?: string;
  lastPrice?: string;
}

interface BybitWsMsg {
  op?: string;
  topic?: string;
  type?: "snapshot" | "delta";
  data?: BybitTickerData;
  ts?: number;
}

function makeBybitAdapter(wsUrl: string, isSpot: boolean): ExchangeWsAdapter {
  return {
    subscribe(symbol, onUpdate, onError) {
      const normalized = normalizeSymbol(symbol);
      const topic = `tickers.${normalized}`;

      let ws: WebSocket | null = null;
      let pingTimer: ReturnType<typeof setInterval> | null = null;
      let stopped = false;
      let attempt = 0;

      let markPrice = 0;
      let indexPrice = 0;
      let fundingRate = 0;
      let nextFundingTime = 0;

      function clearPing() {
        if (pingTimer !== null) {
          clearInterval(pingTimer);
          pingTimer = null;
        }
      }

      function connect() {
        if (stopped) return;
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          ws?.send(JSON.stringify({ op: "subscribe", args: [topic] }));
          pingTimer = setInterval(() => {
            ws?.send(JSON.stringify({ op: "ping" }));
          }, PING_INTERVAL_MS);
        };

        ws.onmessage = (e: MessageEvent<string>) => {
          try {
            const msg = JSON.parse(e.data) as BybitWsMsg;
            if (!msg.topic?.startsWith("tickers.")) return;

            const d = msg.data;
            if (!d) return;

            if (d.markPrice) markPrice = Number.parseFloat(d.markPrice);
            if (d.indexPrice) indexPrice = Number.parseFloat(d.indexPrice);
            if (d.fundingRate) fundingRate = Number.parseFloat(d.fundingRate);
            if (d.nextFundingTime)
              nextFundingTime = Number.parseInt(d.nextFundingTime, 10);

            if (isSpot && d.lastPrice) {
              const lp = Number.parseFloat(d.lastPrice);
              markPrice = lp;
              indexPrice = lp;
            }

            if (markPrice <= 0) return;

            attempt = 0;
            onUpdate({
              symbol: normalized,
              exchange: "bybit",
              markPrice,
              indexPrice,
              fundingRate,
              nextFundingTime,
              timestamp: msg.ts ?? Date.now(),
            });
          } catch {
            // ignore parse errors
          }
        };

        ws.onerror = () => {
          // will be followed by close
        };

        ws.onclose = () => {
          clearPing();
          if (stopped) return;
          const delay =
            RECONNECT_DELAYS_MS[
              Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
            ];
          attempt++;
          if (attempt > RECONNECT_DELAYS_MS.length) {
            onError?.(new Error(`Bybit WS closed after ${attempt} attempts`));
            return;
          }
          setTimeout(connect, delay);
        };
      }

      connect();

      return () => {
        stopped = true;
        clearPing();
        ws?.close();
        ws = null;
      };
    },
  };
}

export const bybitLinearWsAdapter: ExchangeWsAdapter = makeBybitAdapter(
  "wss://stream.bybit.com/v5/public/linear",
  false
);

export const bybitSpotWsAdapter: ExchangeWsAdapter = makeBybitAdapter(
  "wss://stream.bybit.com/v5/public/spot",
  true
);
